import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Message } from "primereact/message";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { useAuth } from "../../../AuthContext";
import {
  FaUserTie,
  FaEdit,
  FaTrash,
  FaPlus,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaGavel,
} from "react-icons/fa";

const API_BASE_URL = "https://sapl.cantarellisolucoes.com.br/wp-json/custom/v1";

const MesaDiretora = () => {
  const { user } = useAuth();
  const toast = useRef(null);

  // Estados
  const [loading, setLoading] = useState(false);
  const [legislaturas, setLegislaturas] = useState([]);
  const [legislaturaSelecionada, setLegislaturaSelecionada] = useState(null);
  const [mesaData, setMesaData] = useState(null);
  const [membrosMesa, setMembrosMesa] = useState([]);
  const [parlamentares, setParlamentares] = useState([]);
  const [parlamentaresDisponiveis, setParlamentaresDisponiveis] = useState([]);

  // Estados de filtro
  const [termoPesquisa, setTermoPesquisa] = useState("");
  const [apenasAtivos, setApenasAtivos] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  // Estados do modal
  const [modalVisible, setModalVisible] = useState(false);
  const [editandoMembro, setEditandoMembro] = useState(null);
  const [formData, setFormData] = useState({
    parlamentar_id: null,
    cargo: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Opções de cargos (conforme regras do SAPL)
  const cargosDisponiveis = [
    { label: "Presidente", value: "Presidente" },
    { label: "Vice-Presidente", value: "Vice-Presidente" },
    { label: "1º Secretário", value: "1º Secretário" },
    { label: "2º Secretário", value: "2º Secretário" },
    { label: "3º Secretário", value: "3º Secretário" },
  ];

  // Função genérica para requisições fetch com token
  const apiRequest = async (endpoint, options = {}) => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
        ...options.headers,
      },
      credentials: "include",
    });

    const data = await response.json();

    if (!response.ok) {
      throw { status: response.status, data };
    }

    return data;
  };

  // Carregar legislaturas da API
  const carregarLegislaturas = useCallback(async () => {
    try {
      const response = await apiRequest("/legislaturas");
      const legislaturasList = response.data.map((leg) => ({
        id: leg.id,
        name: leg.descricao,
        code: leg.numero,
        atual: leg.ativa === 1,
        data_inicio: leg.data_inicio,
        data_fim: leg.data_fim,
      }));
      setLegislaturas(legislaturasList);

      // Seleciona a legislatura atual por padrão
      const atual = legislaturasList.find((l) => l.atual);
      if (atual) setLegislaturaSelecionada(atual);
      else if (legislaturasList[0])
        setLegislaturaSelecionada(legislaturasList[0]);
    } catch (error) {
      console.error("Erro ao carregar legislaturas:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar as legislaturas",
        life: 5000,
      });
    }
  }, [user?.token]);

  // Carregar parlamentares ativos da legislatura selecionada
  const carregarParlamentares = useCallback(async () => {
    try {
      let url = "/parlamentares?ativo=true";
      if (legislaturaSelecionada?.id) {
        url += `&legislatura_id=${legislaturaSelecionada.id}`;
      }
      const response = await apiRequest(url);
      const todosParlamentares = response.data || [];

      // console.log("todosParlamentares = ", todosParlamentares);
      setParlamentares(todosParlamentares);

      // Filtra parlamentares que ainda não estão na mesa
      const idsNaMesa = membrosMesa.map((m) => m.parlamentar_id);
      let disponiveis = todosParlamentares.filter(
        (p) => !idsNaMesa.includes(p.id),
      );

      // Aplica filtro de pesquisa
      if (termoPesquisa) {
        const termo = termoPesquisa.toLowerCase();
        disponiveis = disponiveis.filter(
          (p) =>
            p.nome?.toLowerCase().includes(termo) ||
            p.nome_parlamentar?.toLowerCase().includes(termo) ||
            p.partido?.toLowerCase().includes(termo),
        );
      }

      // Aplica filtro de ativos
      if (apenasAtivos) {
        disponiveis = disponiveis.filter(
          (p) => p.ativo === 1 || p.ativo === true,
        );
      }

      setParlamentaresDisponiveis(disponiveis);
    } catch (error) {
      console.error("Erro ao carregar parlamentares:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: "Não foi possível carregar os parlamentares",
        life: 5000,
      });
    }
  }, [
    user?.token,
    legislaturaSelecionada,
    membrosMesa,
    termoPesquisa,
    apenasAtivos,
  ]);

  // Carregar composição da mesa por legislatura
  const carregarMesaDiretora = useCallback(async () => {
    if (!legislaturaSelecionada) return;

    setLoading(true);
    try {
      const response = await apiRequest(
        `/mesa-diretora/legislatura-id/${legislaturaSelecionada.id}`,
      );
      setMesaData(response.data);
      setMembrosMesa(response.data?.membros || []);
    } catch (error) {
      if (error.status === 404) {
        setMesaData(null);
        setMembrosMesa([]);
      } else {
        console.error("Erro ao carregar mesa diretora:", error);
        toast.current?.show({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível carregar a composição da mesa",
          life: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  }, [legislaturaSelecionada, user?.token]);

  // Efeitos de carregamento inicial
  useEffect(() => {
    if (user?.token) {
      carregarLegislaturas();
    }
  }, [user?.token, carregarLegislaturas]);

  useEffect(() => {
    if (legislaturaSelecionada && user?.token) {
      carregarMesaDiretora();
    }
  }, [legislaturaSelecionada, carregarMesaDiretora, user?.token]);

  useEffect(() => {
    if (user?.token && legislaturaSelecionada) {
      carregarParlamentares();
    }
  }, [
    user?.token,
    legislaturaSelecionada,
    membrosMesa,
    carregarParlamentares,
    termoPesquisa,
    apenasAtivos,
  ]);

  // Verificar se um cargo já está ocupado
  const cargoOcupado = (cargo, membroId = null) => {
    return membrosMesa.some(
      (m) => m.cargo === cargo && (membroId === null || m.id !== membroId),
    );
  };

  // Verificar se um parlamentar já está na mesa
  const parlamentarJaNaMesa = (parlamentarId, membroId = null) => {
    return membrosMesa.some(
      (m) =>
        m.parlamentar_id === parlamentarId &&
        (membroId === null || m.id !== membroId),
    );
  };

  // Abrir modal para adicionar membro
  const abrirModalAdicionar = () => {
    setEditandoMembro(null);
    setFormData({
      parlamentar_id: null,
      cargo: "",
    });
    setModalVisible(true);
  };

  // Abrir modal para editar membro
  const abrirModalEditar = (membro) => {
    setEditandoMembro(membro);
    setFormData({
      parlamentar_id: membro.parlamentar_id,
      cargo: membro.cargo,
    });
    setModalVisible(true);
  };

  // Criar nova mesa diretora
  const criarMesaDiretora = async (membro) => {
    const response = await apiRequest("/mesa-diretora", {
      method: "POST",
      body: JSON.stringify({
        legislatura_id: legislaturaSelecionada.id,
        legislatura: legislaturaSelecionada.name,
        data_inicio:
          legislaturaSelecionada.data_inicio ||
          new Date().toISOString().split("T")[0],
        data_fim: legislaturaSelecionada.data_fim,
        membros: [
          {
            parlamentar_id: membro.parlamentar_id,
            cargo: membro.cargo,
          },
        ],
      }),
    });
    return response.data;
  };

  // Adicionar membro à mesa existente
  const adicionarMembroMesa = async (mesaId, membro) => {
    const response = await apiRequest(`/mesa-diretora/${mesaId}/membros`, {
      method: "POST",
      body: JSON.stringify({
        parlamentar_id: membro.parlamentar_id,
        cargo: membro.cargo,
      }),
    });
    return response.data;
  };

  // Atualizar membro
  const atualizarMembroMesa = async (membroId, membro) => {
    const response = await apiRequest(`/mesa-membros/${membroId}`, {
      method: "PUT",
      body: JSON.stringify({
        cargo: membro.cargo,
      }),
    });
    return response.data;
  };

  // Remover membro
  const removerMembroMesa = async (membroId) => {
    await apiRequest(`/mesa-membros/${membroId}`, {
      method: "DELETE",
    });
  };

  // Salvar membro (adicionar ou editar)
  const salvarMembro = async () => {
    if (!formData.parlamentar_id) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione um parlamentar",
        life: 3000,
      });
      return;
    }

    if (!formData.cargo) {
      toast.current?.show({
        severity: "warn",
        summary: "Atenção",
        detail: "Selecione um cargo",
        life: 3000,
      });
      return;
    }

    // Validação de cargo único
    if (cargoOcupado(formData.cargo, editandoMembro?.id)) {
      toast.current?.show({
        severity: "error",
        summary: "Cargo ocupado",
        detail: `O cargo ${formData.cargo} já está atribuído a outro membro`,
        life: 4000,
      });
      return;
    }

    // Validação de parlamentar único (apenas para novos membros)
    if (!editandoMembro && parlamentarJaNaMesa(formData.parlamentar_id)) {
      toast.current?.show({
        severity: "error",
        summary: "Parlamentar já na mesa",
        detail: "Este parlamentar já faz parte da mesa diretora",
        life: 4000,
      });
      return;
    }

    setSubmitting(true);

    try {
      if (editandoMembro) {
        await atualizarMembroMesa(editandoMembro.id, {
          cargo: formData.cargo,
        });

        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Membro atualizado com sucesso",
          life: 3000,
        });
      } else {
        if (!mesaData) {
          const novaMesa = await criarMesaDiretora({
            parlamentar_id: formData.parlamentar_id,
            cargo: formData.cargo,
          });
          setMesaData(novaMesa);
        } else {
          await adicionarMembroMesa(mesaData.id, {
            parlamentar_id: formData.parlamentar_id,
            cargo: formData.cargo,
          });
        }

        toast.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Membro adicionado à mesa diretora",
          life: 3000,
        });
      }

      setModalVisible(false);
      carregarMesaDiretora();
    } catch (error) {
      console.error("Erro ao salvar membro:", error);
      toast.current?.show({
        severity: "error",
        summary: "Erro",
        detail: error.data?.message || "Falha ao salvar membro",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Remover membro
  const confirmarRemocao = (membro) => {
    confirmDialog({
      header: "",
      style: { width: "380px" },
      className: "custom-confirm-dialog",

      message: (
        <div className="flex flex-column align-items-center text-center">
          <i
            className="pi pi-exclamation-triangle"
            style={{ fontSize: "3rem", color: "#f59e0b", marginBottom: "1rem" }}
          ></i>

          <span
            style={{
              fontSize: "1.1rem",
              fontWeight: "500",
              marginBottom: "0.5rem",
            }}
          >
            Confirmar remoção
          </span>

          <span style={{ fontSize: "0.95rem", color: "#6b7280" }}>
            Deseja realmente <strong>remover</strong> o parlamentar
          </span>

          <span
            style={{ fontSize: "1rem", fontWeight: "600", marginTop: "0.5rem" }}
          >
            {membro.nome}?
          </span>
        </div>
      ),

      footer: (options) => (
        <div className="flex justify-content-center gap-2 w-full">
          <button
            className="p-button p-component p-button-sm p-button-secondary"
            onClick={options.reject}
          >
            <span className="p-button-label">Cancelar</span>
          </button>

          <button
            className="p-button p-component p-button-sm p-button-danger"
            onClick={options.accept}
          >
            <span className="p-button-label">Remover</span>
          </button>
        </div>
      ),

      accept: async () => {
        setLoading(true);
        try {
          await removerMembroMesa(membro.id);
          toast.current?.show({
            severity: "success",
            summary: "Removido",
            detail: `${membro.nome} foi removido da mesa diretora`,
            life: 3000,
          });
          carregarMesaDiretora();
        } catch (error) {
          console.error("Erro ao remover membro:", error);
          toast.current?.show({
            severity: "error",
            summary: "Erro",
            detail: error.data?.message || "Falha ao remover membro",
            life: 5000,
          });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Templates da tabela
  const nomeBody = (rowData) => (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          backgroundColor: "#f3f4f6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#3b82f6",
          fontSize: "1.2rem",
        }}
      >
        <FaUserTie />
      </div>
      <div>
        <div style={{ fontWeight: "500" }}>{rowData.nome}</div>
        {rowData.nome_parlamentar && (
          <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
            Urna: {rowData.nome_parlamentar}
          </div>
        )}
      </div>
    </div>
  );

  const partidoBody = (rowData) => {
    const isSemFiliacao =
      rowData.partido === "Não possui filiação" || !rowData.partido;
    return (
      <Tag
        value={rowData.partido || "Sem partido"}
        severity={isSemFiliacao ? "secondary" : "info"}
        icon={isSemFiliacao ? "pi pi-user" : "pi pi-flag"}
      />
    );
  };

  const cargoBody = (rowData) => {
    const getCargoConfig = (cargo) => {
      const configs = {
        Presidente: { severity: "success", icon: "pi pi-star" },
        "Vice-Presidente": { severity: "info", icon: "pi pi-star-fill" },
        "1º Secretário": { severity: "warning", icon: "pi pi-pen" },
        "2º Secretário": { severity: "help", icon: "pi pi-pen" },
        "3º Secretário": { severity: "secondary", icon: "pi pi-pen" },
      };
      return configs[cargo] || { severity: "info", icon: "pi pi-user" };
    };

    const config = getCargoConfig(rowData.cargo);

    return (
      <Tag
        value={rowData.cargo}
        severity={config.severity}
        icon={config.icon}
        style={{ fontSize: "0.85rem", padding: "0.4rem 0.8rem" }}
      />
    );
  };

  const acoesBody = (rowData) => (
    <React.Fragment>
      <Button
        icon="pi pi-file-edit"
        size="small"
        severity="success"
        tooltip="Editar"
        tooltipOptions={{ position: "left" }}
        className="mr-2 custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => abrirModalEditar(rowData)}
      />
      <Button
        icon="pi pi-trash"
        size="small"
        severity="danger"
        tooltip="Remover"
        tooltipOptions={{ position: "left" }}
        className="custom-button button-efeito"
        style={{ width: "32px", height: "32px" }}
        onClick={() => confirmarRemocao(rowData)}
      />
    </React.Fragment>
  );

  const emptyMessage = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "0 20px",
        color: "#6b7280",
      }}
    >
      <FaGavel size={48} style={{ color: "#9ca3af", marginBottom: "20px" }} />
      <h3 style={{ marginBottom: "8px", color: "#374151" }}>
        Nenhum membro encontrado
      </h3>
      <p
        style={{ textAlign: "center", maxWidth: "400px", fontSize: "0.85rem" }}
      >
        Não há membros da mesa diretora para a legislatura selecionada.
      </p>
    </div>
  );

  const modalFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        size="small"
        severity="secondary"
        icon="pi pi-times"
        onClick={() => setModalVisible(false)}
        outlined
      />
      <Button
        label="Salvar"
        size="small"
        severity="success"
        icon="pi pi-check"
        onClick={salvarMembro}
        loading={submitting}
        autoFocus
      />
    </div>
  );

  const totalMembros = membrosMesa.length;

  return (
    <div className="dashboard-container">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="dashboard-header">
        <h1>Mesa Diretora</h1>
        <p>Gestão da mesa diretora do legislativo</p>
      </div>

      <div className="page-content">
        {/* Tabela da mesa diretora */}
        <Card style={{ padding: "0" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            {/* ESQUERDA */}
            <div style={{ minWidth: "250px" }}>
              <Dropdown
                value={legislaturaSelecionada}
                options={legislaturas}
                onChange={(e) => setLegislaturaSelecionada(e.value)}
                optionLabel="name"
                placeholder="Selecione uma legislatura"
                loading={!legislaturas.length}
                style={{ width: "100%" }}
              />
            </div>

            {/* DIREITA */}
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                label="Adicionar"
                icon="pi pi-plus"
                severity="success"
                size="small"
                onClick={abrirModalAdicionar}
                disabled={loading || parlamentaresDisponiveis.length === 0}
              />
              <Button
                label="Exportar"
                icon="pi pi-file-excel"
                severity="secondary"
                size="small"
                outlined
                onClick={() => console.log("Exportar...")}
              />
            </div>
          </div>

          <div style={{ padding: "0 0 10px 0", width: "100%" }}>
            <Message
              severity="info"
              text={`Legislatura: ${legislaturaSelecionada?.name || "Selecione uma legislatura"} ${legislaturaSelecionada?.atual ? "(Atual)" : ""} • Total de Membros: ${totalMembros}`}
              style={{ width: "100%" }}
            />
          </div>

          {loading ? (
            <div className="loading-container">
              <ProgressSpinner />
            </div>
          ) : (
            <DataTable
              value={membrosMesa}
              dataKey="id"
              style={{ width: "100%" }}
              paginator
              rows={10}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="{first} a {last} de {totalRecords}"
              emptyMessage={emptyMessage}
              showGridlines
              stripedRows
            >
              <Column
                field="nome"
                header="Parlamentar"
                body={nomeBody}
                sortable
                style={{ minWidth: "280px" }}
              />
              <Column
                field="partido"
                header="Partido"
                body={partidoBody}
                sortable
                style={{ minWidth: "180px" }}
              />
              <Column
                field="cargo"
                header="Cargo"
                body={cargoBody}
                sortable
                style={{ minWidth: "180px" }}
              />
              <Column
                header="Ações"
                body={acoesBody}
                style={{ width: "120px" }}
              />
            </DataTable>
          )}
        </Card>
      </div>

      {/* Modal de Adicionar/Editar Membro */}
      <Dialog
        visible={modalVisible}
        style={{ width: "650px", maxWidth: "90vw" }}
        modal
        className="p-fluid modern-dialog"
        onHide={() => setModalVisible(false)}
        footer={modalFooter}
        header={
          <div className="flex align-items-center gap-2">
            <div
              className="bg-primary bg-opacity-10 border-circle p-2"
              style={{ borderRadius: "12px" }}
            >
              <i
                className={`pi ${editandoMembro ? "pi-user-edit" : "pi-user-plus"} text-primary text-xl`}
              ></i>
            </div>
            <span className="font-bold text-xl">
              {editandoMembro ? "Editar Membro" : "Adicionar Membro"}
            </span>
          </div>
        }
      >
        <div className="modal-form" style={{ padding: 0 }}>
          <div
            className="grid p-fluid"
            style={{
              backgroundColor: "#fafafa",
              border: "1px solid #e8e8e8",
              padding: "15px",
              borderRadius: "12px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
              margin: 0,
            }}
          >
            {!editandoMembro && (
              <div className="col-12">
                <div className="field" style={{ marginBottom: "15px" }}>
                  <label className="font-semibold mb-1">Parlamentar *</label>
                  <Dropdown
                    value={formData.parlamentar_id}
                    options={parlamentaresDisponiveis.map((p) => ({
                      label: `${p.nome}${p.nome_parlamentar ? ` (${p.nome_parlamentar})` : ""} - ${p.partido || "Sem partido"}`,
                      value: p.id,
                    }))}
                    onChange={(e) =>
                      setFormData({ ...formData, parlamentar_id: e.value })
                    }
                    placeholder="Selecione um parlamentar"
                    filter
                    showClear
                  />
                </div>
              </div>
            )}

            <div className="col-12">
              <div className="field" style={{ marginBottom: 0 }}>
                <label className="font-semibold mb-1">Cargo *</label>
                <Dropdown
                  value={formData.cargo}
                  options={cargosDisponiveis}
                  onChange={(e) => setFormData({ ...formData, cargo: e.value })}
                  placeholder="Selecione um cargo"
                />
                <small
                  className="p-text-secondary"
                  style={{ color: "#6b7280", fontSize: "0.75rem" }}
                >
                  Cada cargo pode ser ocupado por apenas um parlamentar
                </small>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default MesaDiretora;
